import { Box, Flex, Icon, Tooltip, useDisclosure } from "@chakra-ui/react";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { IconButton, useTheme } from "../../../components";
import useCurrentBreakpoint from "../../../hooks/useCurrentBreakpoint";
import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import { useSendGAEvent } from "../../../utils/googleAnalytics";
import {
  CallNoteFragment,
  CallNoteType,
  CallSpeakerFragment,
  CallSpeakerOption,
  ShareUserFragment,
  TranscriptSegmentFragment,
} from "../../graphql";
import { VideoDisplayMode } from "../../types";
import { type IntegrityTimelineEvent } from "../AiRecording/IntegritySignalCard/utils";
import {
  CallbackRef,
  MediaListeners,
  MediaPlayerInterface,
} from "../MediaPlayer/useMediaPlayer";
import {
  VideoCallbackRef,
  VideoStateControl,
} from "../MediaPlayer/useVideoState";
import { videoElementComplete } from "../MediaPlayer/utils";
import { ActionBarProps } from "../Recording/ActionBar/ActionBar";
import ActionBarNoteSection from "../Recording/ActionBar/ActionBarNoteSection";
import { IntegrityBarV2 } from "../Recording/IntegrityBarV2";
import SpeakerBarsV2 from "../Recording/SpeakerBarsV2";
import ActionBarV2 from "./ActionBarV2";
import ExpandedActionRow from "./ExpandedActionRow";
import FullscreenNotification from "./FullscreenNotification";
import OverflowActionBar from "./OverflowActionBar";
import PlayerWithDisplayModeMessageV2 from "./PlayerWithDisplayModeMessageV2";
import {
  CONTROL_ACTIVE_BG,
  CONTROL_HOVER_BG,
  IconHideVideo,
  IconShowVideo,
} from "./theme";
import { VideoControlsMobileV2 } from "./VideoControlsMobileV2";
import { VideoControlsV2 } from "./VideoControlsV2";
import VideoFullscreenControlsV2 from "./VideoFullscreenControlsV2";
import VideoPlayerProgressBarV2, {
  type VideoPlayerProgressBarV2Props,
} from "./VideoPlayerProgressBarV2";

/** Width threshold for switching to compact/overflow action bar */
const NARROW_VIEWPORT_THRESHOLD = 484;

/** Height ratio to match legacy MediaPlayer height constraints */
export const MEDIA_VIDEO_HEIGHT_RATIO = 0.36;

type RenderActionBarProps = ActionBarProps & {
  showVideo?(): void;
};

export interface MediaPlayerV2Props {
  callId?: string;
  speakers: CallSpeakerFragment[];
  speakerOptions?: Pick<CallSpeakerOption, "id" | "label">[];
  canChangeSpeaker?: boolean;
  canExpandSpeakerBars?: boolean;
  duration?: number | null;
  listeners: MediaListeners;
  mediaPlayerRef: CallbackRef;
  mediaSrc?: string;
  notes: CallNoteFragment[];
  cameraTimestamps?: Array<{ timestamp: number; enabled: boolean }>;
  integrityEvents?: IntegrityTimelineEvent[];
  onSeek(t: number): void;
  onCreateClipClick?(): void;
  onAddNote?(note: {
    text: string;
    type: CallNoteType;
    time: number;
  }): Promise<void>;
  player: MediaPlayerInterface;
  videoDisplayMode: VideoDisplayMode;
  segments: TranscriptSegmentFragment[];
  shareableUsers?: ShareUserFragment[] | undefined;
  shareableUsersLoading?: boolean;
  isVideoVisible: boolean;
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  videoRef: VideoCallbackRef;
  hideVideo(enablePIP?: boolean): void;
  showVideo?(): void;
  fullScreenControls: VideoStateControl;
  pipControls: VideoStateControl;
  isActionBarEnabled?: boolean;
  renderActionBar?: (props: RenderActionBarProps) => React.ReactNode;

  /** For storybook: force the overflow action row to stay expanded */
  forceExpandedActionRow?: boolean;
  /** For storybook: force small screen (mobile) behavior */
  forceSmallScreen?: boolean;
  /** For storybook: force speaker bars to start expanded */
  forceSpeakerBarsExpanded?: boolean;
  /**
   * Aspect ratio multiplier for calculating container height.
   * Default is 9/16 (0.5625) for true 16:9 video.
   * Use 0.4 to match legacy MediaPlayer height constraints.
   */
  heightRatio?: number;
}

export const MediaPlayerV2: React.FC<MediaPlayerV2Props> = ({
  callId,
  speakers,
  speakerOptions,
  canChangeSpeaker = false,
  canExpandSpeakerBars = true,
  duration,
  listeners,
  mediaPlayerRef,
  mediaSrc,
  notes,
  cameraTimestamps = [],
  integrityEvents = [],
  onSeek,
  onAddNote,
  onCreateClipClick,
  player,
  videoDisplayMode,
  segments,
  shareableUsers,
  shareableUsersLoading,
  isVideoVisible,
  containerRef,
  videoRef,
  hideVideo,
  showVideo,
  fullScreenControls,
  pipControls,
  isActionBarEnabled = true,
  renderActionBar,
  forceExpandedActionRow = false,
  forceSmallScreen = false,
  forceSpeakerBarsExpanded = false,
  heightRatio = 9 / 16,
}) => {
  const isSmallScreenActual = useIsSmallScreen();
  const isSmallScreen = forceSmallScreen || isSmallScreenActual;
  const currentBreakpoint = useCurrentBreakpoint();
  const sendGAEvent = useSendGAEvent();
  const { colors } = useTheme();

  const { isActive: fullscreenActive } = fullScreenControls;
  const { isActive: pipActive } = pipControls;

  // Use the provided aspect ratio multiplier (default 9/16 = 0.5625 for true 16:9)
  const ASPECT_RATIO_MULTIPLIER = heightRatio;

  const [height, setHeight] = useState<string | number>(
    ASPECT_RATIO_MULTIPLIER * Math.floor(window.innerWidth)
  );

  useLayoutEffect(() => {
    if (fullscreenActive) {
      setHeight(`calc(100% - 32px)`);
    } else if (containerRef.current?.clientWidth) {
      setHeight(
        ASPECT_RATIO_MULTIPLIER * (containerRef.current?.clientWidth || 0)
      );
    }
  }, [fullscreenActive]);

  useLayoutEffect(() => {
    const updateSize = (): void => {
      if (!fullscreenActive) {
        setHeight(
          ASPECT_RATIO_MULTIPLIER * (containerRef.current?.clientWidth || 0)
        );
      }
    };
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, [fullscreenActive]);

  const { time, duration: playerDuration } = player;
  const [isNoteSectionOpen, setIsNoteSectionOpen] = useState(false);
  const [noteType, setNoteType] = useState(CallNoteType.Note);

  // Track container width for responsive action bar
  const [containerWidth, setContainerWidth] = useState<number>(
    containerRef.current?.clientWidth || window.innerWidth
  );
  const [isActionRowExpanded, setIsActionRowExpanded] = useState(false);
  const isNarrowViewport = containerWidth <= NARROW_VIEWPORT_THRESHOLD;
  const showSpeakerBars = currentBreakpoint !== "base";

  // Compute effective expanded state (forced or user-toggled)
  const effectiveActionRowExpanded =
    forceExpandedActionRow || isActionRowExpanded;

  // Update container width on resize
  useLayoutEffect(() => {
    const updateWidth = (): void => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Collapse action row when switching to wide viewport (unless forced)
  useEffect(() => {
    if (!isNarrowViewport && !forceExpandedActionRow) {
      setIsActionRowExpanded(false);
    }
  }, [isNarrowViewport, forceExpandedActionRow]);

  const toggleActionRowExpanded = useCallback(() => {
    setIsActionRowExpanded((prev) => !prev);
  }, []);

  // In-player notification for fullscreen mode (toasts don't render in fullscreen)
  const [notification, setNotification] = useState<string | null>(null);
  const notificationTimeoutRef = useRef<NodeJS.Timeout>();

  const showNotification = useCallback((message: string) => {
    clearTimeout(notificationTimeoutRef.current);
    setNotification(message);
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification(null);
    }, 2000);
  }, []);

  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  const openNote =
    (type: CallNoteType): (() => void) =>
    () => {
      sendGAEvent("action_bar", "call_review", type);
      /**
       * Allow one-click star and flag notes, but for
       * a general note open the text input
       */
      if (!onAddNote) {
        return;
      }
      if ([CallNoteType.Star, CallNoteType.Flag].includes(type)) {
        onAddNote({ text: "", type, time }).then(() => {
          const label =
            type === CallNoteType.Star ? "Moment starred" : "Red flag added";
          showNotification(label);
        });
      } else {
        if (isNoteSectionOpen) {
          setIsNoteSectionOpen(false);
          return;
        }
        setNoteType(type);
        setIsNoteSectionOpen(true);
      }
    };

  const {
    isOpen: isHovering,
    onOpen: onMouseEnter,
    onClose: onMouseLeave,
  } = useDisclosure({
    // Disable hover tracking for mobile devices
    isOpen: isSmallScreen ? false : undefined,
  });

  const progressBarProps: VideoPlayerProgressBarV2Props = {
    duration: playerDuration ?? 0,
    value: time,
    speakers,
    seek: onSeek,
    disabled: false,
    notes,
    cameraTimestamps,
  };

  const videoReady = mediaSrc && videoElementComplete(player, videoDisplayMode);
  const shouldUseMobileControls = isSmallScreen && isVideoVisible && videoReady;
  const shouldUseDesktopControls = !shouldUseMobileControls && videoReady;

  const [shouldShowScreenControls, setShouldShowScreenControls] =
    useState(true);
  const hideScreenControlsTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      if (hideScreenControlsTimeoutRef.current) {
        clearTimeout(hideScreenControlsTimeoutRef.current);
      }
    };
  }, []);

  const onVideoPlayerClick = useCallback(() => {
    if (isSmallScreen) {
      if (player.playing && !pipActive) {
        setShouldShowScreenControls(true);
        clearTimeout(hideScreenControlsTimeoutRef.current);
        hideScreenControlsTimeoutRef.current = setTimeout(() => {
          setShouldShowScreenControls(false);
        }, 3000);
      }
    } else if (player.playing) {
      player.pause();
    } else {
      player.play();
    }
  }, [player.playing, pipActive, player, isSmallScreen]);

  /**
   * Screen control logic
   *  - show if no video or if paused
   *  - show on hover if isDesktop and video is playing
   *  - show on tap if isSmallScreen and video is playing
   *     - This is handled above; the isMobile case in
   *       onVideoPlayerClick shows the controls on tap
   */
  useEffect(() => {
    if (!isVideoVisible || !player.playing) {
      setShouldShowScreenControls(true);
      clearTimeout(hideScreenControlsTimeoutRef.current);
    } else if (!isSmallScreen) {
      setShouldShowScreenControls(isHovering);
    } else if (isSmallScreen) {
      // On mobile, controls are hidden when playing, shown on tap (handled in onVideoPlayerClick)
      setShouldShowScreenControls(false);
    }
  }, [isVideoVisible, player.playing, isHovering, isSmallScreen]);

  // On mobile only, when PIP is active, hide the video controls
  useEffect(() => {
    if (isSmallScreen && pipActive) {
      setShouldShowScreenControls(false);
    }
  }, [pipActive, isSmallScreen]);

  return (
    <Box
      ref={containerRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      bg={
        isVideoVisible || pipActive
          ? "transparent"
          : "linear-gradient(180deg, #010D17 0%, #012A4F 100%);"
      }
      overflow={videoReady ? undefined : "hidden"}
      sx={{
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        textRendering: "optimizeLegibility",
        imageRendering: "smooth",
      }}
    >
      <Flex
        position="relative"
        height={isVideoVisible || pipActive ? height : "auto"}
        minH={isVideoVisible || pipActive ? "220px" : undefined}
      >
        <PlayerWithDisplayModeMessageV2
          mediaPlayerRef={mediaPlayerRef}
          videoRef={videoRef}
          clipInProgress={false}
          clipTimedOut={false}
          listeners={listeners}
          videoDisplayMode={videoDisplayMode}
          mediaSrc={mediaSrc}
          height="100%"
          isVideoVisible={isVideoVisible}
          pipActive={pipActive}
          currentTime={time}
          cameraTimestamps={cameraTimestamps}
          onClick={onVideoPlayerClick}
          onExitPip={showVideo}
          objectFit={isSmallScreen ? "cover" : undefined}
        />
        {/* In-player notification for fullscreen mode */}
        {notification && <FullscreenNotification message={notification} />}
        {shouldUseDesktopControls && !fullscreenActive && isVideoVisible && (
          <Box position="absolute" top="3" right="4">
            <Tooltip label="Hide video">
              <IconButton
                icon={<Icon as={IconHideVideo} boxSize="6" />}
                aria-label="Hide video"
                borderRadius="full"
                w={10}
                h={10}
                minW={10}
                color="white"
                bg="blackAlpha.600"
                _hover={{ bg: CONTROL_HOVER_BG }}
                _active={{ bg: CONTROL_ACTIVE_BG }}
                onClick={() => hideVideo(false)}
              />
            </Tooltip>
          </Box>
        )}
        {/* Action Bar - anchored to right-center when video is visible */}
        {shouldUseDesktopControls &&
          isVideoVisible &&
          isActionBarEnabled &&
          !renderActionBar && (
            <Box
              position="absolute"
              right="4"
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="auto"
            >
              <ActionBarV2
                onCreateClipClick={onCreateClipClick}
                openNote={openNote}
                isVideoVisible={isVideoVisible}
              />
            </Box>
          )}
        {shouldUseDesktopControls ? (
          <Flex
            alignItems="flex-start"
            pos={isVideoVisible || pipActive ? "absolute" : "static"}
            left="0"
            right="0"
            bottom="0"
            flexWrap="wrap-reverse"
            justifyContent="space-between"
            minWidth="0"
            pointerEvents="none"
            width="100%"
          >
            <Flex
              direction="column"
              h={isVideoVisible || pipActive ? "120px" : "auto"}
              justifyContent="flex-end"
              px="4"
              pt="4"
              pb="2"
              width="100%"
              bg={
                shouldShowScreenControls && (isVideoVisible || pipActive)
                  ? `linear-gradient(transparent, ${colors.blackAlpha[800]})`
                  : "transparent"
              }
            >
              {shouldShowScreenControls ? (
                <Flex
                  width="100%"
                  alignItems="center"
                  justifyContent="space-between"
                  pointerEvents="auto"
                >
                  {/* Left: Main Controls */}
                  <Flex flexShrink={0}>
                    <VideoControlsV2
                      player={player}
                      isVideoVisible={isVideoVisible}
                      isSmallScreen={isSmallScreen}
                      containerWidth={containerWidth}
                      zIndex="3"
                      width="auto"
                      justifyContent="flex-start"
                    />
                  </Flex>

                  {/* Right: Action Bar + Show Video Button + Fullscreen Controls */}
                  <Flex alignItems="center" gap="2" flexShrink={0}>
                    {/* Action Bar - switches between full and overflow based on width */}
                    {isActionBarEnabled &&
                      (renderActionBar ? (
                        renderActionBar({
                          onCreateClipClick,
                          openNote,
                          isVideoVisible,
                          showVideo,
                        })
                      ) : isNarrowViewport && !isVideoVisible ? (
                        <OverflowActionBar
                          isVideoVisible={isVideoVisible}
                          onCreateClipClick={onCreateClipClick}
                          openNote={openNote}
                          showVideo={showVideo}
                          isExpanded={effectiveActionRowExpanded}
                          onToggleExpanded={toggleActionRowExpanded}
                        />
                      ) : (
                        <>
                          {/* ActionBarV2 in bottom controls only when video is hidden and wide  viewport */}
                          {!isVideoVisible && !isNarrowViewport && (
                            <ActionBarV2
                              onCreateClipClick={onCreateClipClick}
                              openNote={openNote}
                              isVideoVisible={isVideoVisible}
                            />
                          )}
                          {/* Show Video Button - only in wide viewport when video hidden */}
                          {!isVideoVisible && showVideo && (
                            <Tooltip label="Show video">
                              <IconButton
                                icon={<Icon as={IconShowVideo} boxSize="6" />}
                                aria-label="Show video"
                                borderRadius="full"
                                w={10}
                                h={10}
                                minW={10}
                                color="white"
                                bg="rgba(255, 255, 255, 0.0)"
                                onClick={showVideo}
                              />
                            </Tooltip>
                          )}
                        </>
                      ))}

                    {/* Fullscreen Controls (when video visible) */}
                    {isVideoVisible && (
                      <VideoFullscreenControlsV2
                        pipControls={pipControls}
                        fullScreenControls={fullScreenControls}
                        isSmallScreen={isSmallScreen}
                      />
                    )}
                  </Flex>
                </Flex>
              ) : (
                <Flex />
              )}
            </Flex>
          </Flex>
        ) : (
          <Box />
        )}

        {shouldUseMobileControls && shouldShowScreenControls && (
          <VideoControlsMobileV2
            player={player}
            fullScreenControls={fullScreenControls}
            pipControls={pipControls}
            progressBarProps={progressBarProps}
            onClick={onVideoPlayerClick}
            hideVideo={() => hideVideo(false)}
          />
        )}
      </Flex>

      <Flex
        flexDir="column"
        position="relative"
        bg={
          isVideoVisible || pipActive
            ? "linear-gradient(180deg, #010D17 0%, #012A4F 100%);"
            : "transparent"
        }
      >
        {isActionBarEnabled && onAddNote && (
          <ActionBarNoteSection
            isOpen={isNoteSectionOpen}
            isFullscreen={fullscreenActive}
            noteType={noteType}
            onAddNote={onAddNote}
            setIsOpen={setIsNoteSectionOpen}
            time={player.time}
            shareableUsers={shareableUsers}
            shareableUsersLoading={shareableUsersLoading}
          />
        )}

        {/* Expanded action row for narrow viewports */}
        {isActionBarEnabled &&
          isNarrowViewport &&
          effectiveActionRowExpanded &&
          shouldUseDesktopControls && (
            <ExpandedActionRow
              onCreateClipClick={onCreateClipClick}
              openNote={openNote}
            />
          )}

        <Box
          px={5}
          py={0}
          borderTopColor="whiteAlpha.200"
          borderTopWidth="1px"
          display={shouldUseDesktopControls ? undefined : "none"}
        >
          <VideoPlayerProgressBarV2 {...progressBarProps} />
        </Box>

        {integrityEvents.length > 0 &&
          (duration ?? 0) > 0 &&
          shouldUseDesktopControls &&
          showSpeakerBars && (
            <Box mx={4} borderTopColor="whiteAlpha.200" borderTopWidth="1px">
              <IntegrityBarV2
                callDuration={duration ?? 0}
                integrityEvents={integrityEvents}
                onClickEvent={onSeek}
              />
            </Box>
          )}
        <Box
          px={4}
          py={4}
          borderTopColor="whiteAlpha.200"
          borderTopWidth="1px"
          display={
            shouldUseDesktopControls && showSpeakerBars ? "block" : "none"
          }
        >
          <SpeakerBarsV2
            callId={callId}
            callDuration={duration ?? 0}
            speakers={speakers}
            speakerOptions={speakerOptions}
            canChangeSpeaker={canChangeSpeaker}
            canExpandSpeakerBars={canExpandSpeakerBars}
            player={player}
            segments={segments}
            onClickSegment={onSeek}
            defaultIndex={forceSpeakerBarsExpanded ? 0 : undefined}
          />
        </Box>
      </Flex>
    </Box>
  );
};
